import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import Button from '../../../ui/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import StatusBadge from '../../../ui/Status/StatusBadge';
import Stat, { StatGrid } from '../../../ui/Stat/Stat';
import { Field, Input, Select, FormGrid } from '../../../ui/Field/Field';
import { toast } from '../../../ui/feedback';
import RequirePermission from '../../../components/RequirePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import logger from '../../../utils/logger';
import { contractsService } from '../../../services/firestore';
import { archiveService } from '../../../services/archiveService';
import { finesService, errorMessage } from '../../../services/finesService';
import { useAuth } from '../../../contexts/AuthContext';
import { adminService } from '../../../services/adminService';
import { useCompanySettings } from '../../../services/companySettings';
import { UNKNOWN_STATUS } from '../../../services/contractRules';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import InvoiceNumberField, { useInvoiceNumber } from '../../../components/InvoiceNumberField';
import { deskInvoiceAmounts, deskInvoiceHtml, saveDeskInvoicePdf, saveWorkbook, addReportHeader, escapeHtml } from './deskInvoice';
import { NO_TANK_HINT, TANK_READING_HINT, ejarTankText, keepTankAnswer, tanksOfHistory } from './fuelTank.logic';

const FUEL_STREAKS = [0, 12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100];

/**
 * The tank size is the contract's own Ejar `fuelTankSize` (fuelTank.logic.js), never a per-model table: the
 * "Manage model capacities" table, its Excel import and every save by model are gone (owner, 28 Sep 2026). A car
 * Ejar has no tank for is typed for that one calculation only - never saved.
 */
const FuelCalculator = () => {
    const { currentUser } = useAuth();
    const { settings: companySettings } = useCompanySettings();
    const FUEL_PRICE_PER_LITER = companySettings.fuel_price_per_liter;
    const [logs, setLogs] = useState([]);
    const [out, setOut] = useState(0);
    const [inVal, setInVal] = useState(0);
    const [tankCapacity, setTankCapacity] = useState('');
    const [lastDate, setLastDate] = useState('');
    const [, setDateLocked] = useState(false);
    const [booking, setBooking] = useState('');

    const [contractHistory, setContractHistory] = useState([]);
    const [vehicleLogs, setVehicleLogs] = useState({});

    const [contractData, setContractData] = useState(null);
    const [contractSource, setContractSource] = useState('');
    const [error, setError] = useState('');

    const [inputError, setInputError] = useState('');
    const [manualEndDate, setManualEndDate] = useState('');
    // the contract's own drop-off text behind manualEndDate (keeps its time for display); '' = today (open)
    const [endRaw, setEndRaw] = useState('');
    const [showRefModal, setShowRefModal] = useState(false);
    // numbering while the shared counter is off (as before): ALWFQ-F-<typed>, nothing typed = 'ALWFQ-F-'
    const number = useInvoiceNumber(showRefModal, 0, { prefix: 'ALWFQ-F-', blank: '' });
    const [ejarTanks, setEjarTanks] = useState({}); // { [vehicleId]: true } - the tank came from the Ejar contract
    const [tankFromEjar, setTankFromEjar] = useState(false); // the single-row tank came from the Ejar contract
    const [syncedFields, setSyncedFields] = useState({}); // { [contractId_field]: true }
    // the engine's answer per contract ({ tankLitres, plate, error? }), kept for the page's life - except an answer
    // with `error` (the engine or Ejar did not answer), asked again after TANK_RETRY_MS (keepTankAnswer); a failed
    // read is an answer with `error` (the tank is then typed), never a loop
    const tanksByContract = useRef(new Map());   // CONTRACT NO -> { at, failed, promise }
    const [tanksReading, setTanksReading] = useState(false);   // the Ejar tanks of the history are being read
    const fetchSeq = useRef(0);   // moves on every contract typed: a late tank answer of another contract is dropped
    const tankOf = (contractNo) => {
        const key = String(contractNo || '').trim().toUpperCase();
        if (!key) return Promise.resolve(null);
        const kept = tanksByContract.current.get(key);
        if (keepTankAnswer(kept, Date.now())) return kept.promise;
        const entry = { at: Date.now(), failed: false, promise: null };
        entry.promise = finesService.storeFuelTank(key)
            .then((answer) => { if (answer && answer.error) entry.failed = true; return answer; })
            .catch((err) => {
                entry.failed = true;
                logger.warn(`[Fuel] tank of ${key} not read:`, err?.message || err);
                return { contractNo: key, tankLitres: null, error: errorMessage(err) };
            });
        tanksByContract.current.set(key, entry);
        return entry.promise;
    };

    const parseCustomDate = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        let match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (match) {
            const [, day, month, year] = match;
            return `${year}-${month}-${day}`;
        }
        match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})/);
        if (match) {
            const [, day, month, year] = match;
            return `${year}-${month}-${day}`;
        }
        return null;
    };

    const formatDateToDMY = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) return dateStr.split(' ')[0];
        if (dateStr.includes('-')) {
            const [datePart] = dateStr.split('T');
            const parts = datePart.split('-');
            if (parts.length === 3) {
                const [year, month, day] = parts;
                return `${day}/${month}/${year}`;
            }
        }
        return dateStr;
    };

    // A contract pick-up / drop-off for display: "dd/mm/yyyy HH:MM" when the value has a time,
    // "dd/mm/yyyy" when it has none (never an invented time); unreadable text as before.
    const showContractDate = (value) => formatContractDateTime(value) || (typeof value === 'string' ? formatDateToDMY(value) : '');

    useEffect(() => {
        const fetchContracts = async () => {
            const seq = fetchSeq.current;   // this contract's read (the booking effect below moves it)
            if (booking.trim() === '') {
                setContractData(null);
                setContractHistory([]);
                setVehicleLogs({});
                setEjarTanks({});
                setTankFromEjar(false);
                setTanksReading(false);
                setError('');
                setContractSource('');
                setSyncedFields({});
                return;
            }

            try {
                const history = await contractsService.getContractsByBookingNumber(booking.trim());
                
                if (history && history.length > 0) {
                    setContractHistory(history);
                    setContractData(history[0]);
                    setContractSource('EJAR');

                    // --- FETCH ARCHIVE DATA FOR AUTOMATION ---
                    let archiveRecords = [];
                    try {
                        const bookingTerm = booking.trim();
                        const contractNumbers = history.map(c => c['Contract No.'] || c['Contract No'] || c['Agreement']).filter(Boolean);
                        
                        const [byBooking, byContract] = await Promise.all([
                            archiveService.findByBookingNumber(bookingTerm),
                            archiveService.findByContractNumbers([bookingTerm, ...contractNumbers])
                        ]);
                        
                        // Merge and deduplicate raw records
                        const combined = [...byBooking, ...byContract];
                        const seen = new Set();
                        archiveRecords = combined.filter(r => {
                            const key = `${r['Contract No.'] || r['Contract No']}|${r['Plate Number'] || r['Plate No.']}|${r['Transaction Date']}`;
                            if (!seen.has(key)) {
                                seen.add(key);
                                return true;
                            }
                            return false;
                        });
                        
                    } catch (e) {
                        logger.error('Error fetching archive for fuel automation:', e);
                    }

                    const logUpdates = {};
                    const syncUpdates = {};

                    const sortedByDate = [...history].sort((a, b) => {
                        const getTs = (c) => {
                            const d = c['Pick-up Date'];
                            if (!d) return 0;
                            const p = parseCustomDate(d);
                            if (p) return new Date(p).getTime();
                            return new Date(d).getTime() || 0;
                        };
                        return getTs(b) - getTs(a);
                    });

                    const latest = sortedByDate[0];
                    const earliest = sortedByDate[sortedByDate.length - 1];
                    setContractData(latest); 

                    const status = latest['Status'] || UNKNOWN_STATUS;
                    const isClosed = String(status).toLowerCase().includes('close') ||
                        (latest['Drop-off Date'] && latest['Drop-off Date'].trim() !== '');

                    setContractSource(isClosed ? 'Closed Contract' : 'Open Contract');

                    if (earliest && earliest['Pick-up Date']) {
                        const parsedStart = parseCustomDate(earliest['Pick-up Date']);
                        if (parsedStart) {
                            setLastDate(parsedStart);
                            setDateLocked(true);
                        } else {
                            setLastDate('');
                            setDateLocked(false);
                        }
                    }

                    if (isClosed) {
                        const rawEnd = latest['Drop-off Date'] || latest['Close Date'];
                        const parsedEnd = parseCustomDate(rawEnd);
                        if (parsedEnd) { setManualEndDate(parsedEnd); setEndRaw(rawEnd); }
                    } else {
                        setManualEndDate(new Date().toISOString().split('T')[0]);
                        setEndRaw('');
                    }

                    for (const car of history) {
                        const plate = car['Plate No.'] || car['Plate Number'] || '';

                        // Find matching logs in archive for THIS plate strictly
                        const carLogs = archiveRecords.filter(r => {
                            const rPlate = (r['Plate Number'] || r['Plate No.'] || '').toString().trim().replace(/\s+/g, '');
                            const normalizedPlate = plate.toString().trim().replace(/\s+/g, '');
                            return rPlate === normalizedPlate;
                        }).sort((a, b) => new Date(a['Transaction Date']) - new Date(b['Transaction Date']));

                        const openLog = carLogs.find(l => {
                            const type = (l['Check Type'] || '').toLowerCase();
                            return type.includes('open') || type.includes('create');
                        });
                        const closeLog = carLogs.find(l => {
                            const type = (l['Check Type'] || '').toLowerCase();
                            return type.includes('close');
                        });
                        

                        const startFuelRaw = openLog?.['Fuel'] || openLog?.['Fuel Level %'];
                        const endFuelRaw = closeLog?.['Fuel'] || closeLog?.['Fuel Level %'];

                        const parseFuel = (val) => {
                            if (val === undefined || val === null || val === '') return 0;
                            const num = parseFloat(val.toString().replace('%', '').trim());
                            return isNaN(num) ? 0 : num;
                        };

                        const outVal = parseFuel(startFuelRaw);
                        const inVal = parseFuel(endFuelRaw);


                        // Inject Dual E-Check Numbers from archive
                        if (openLog?.['E-Check Number']) {
                            car['E-Check Number Out'] = openLog['E-Check Number'];
                        }
                        if (closeLog?.['E-Check Number']) {
                            car['E-Check Number In'] = closeLog['E-Check Number'];
                        }

                        logUpdates[car.id] = {
                            out: outVal,
                            in: inVal,
                            capacity: ''
                        };

                        if (startFuelRaw !== undefined) syncUpdates[`${car.id}_out`] = true;
                        if (endFuelRaw !== undefined) syncUpdates[`${car.id}_in`] = true;
                    }
                    // the E-Check levels show at once; the tanks follow (below) - a tank read never holds them back
                    if (Object.keys(logUpdates).length > 0) {
                        setVehicleLogs(prev => ({ ...prev, ...logUpdates }));
                    }
                    setEjarTanks({});
                    if (Object.keys(syncUpdates).length > 0) {
                        setSyncedFields(prev => ({ ...prev, ...syncUpdates }));
                    }

                    if (history.length === 1) {
                        const firstCarId = history[0].id;
                        setOut(logUpdates[firstCarId]?.out || 0);
                        setInVal(logUpdates[firstCarId]?.in || 0); // Changed from inVal to in
                        setTankFromEjar(false);
                    }
                    setError('');

                    // The tank of each car from its contract's own Ejar details (the car Ejar describes; a swapped
                    // contract's other car is typed) - owner, 28 Sep 2026. Every contract of the history is asked at
                    // once, not one after another (a tank the store lacks costs one Ejar read): review, 28 Sep 2026.
                    const tankRows = history.map((car) => ({ id: car.id, contractNo: car['Contract No.'], plate: car['Plate No.'] || car['Plate Number'] || '' }));
                    const tankNos = [...new Set(tankRows.map((r) => String(r.contractNo || '').trim().toUpperCase()).filter(Boolean))];
                    if (tankNos.length > 0) {
                        setTanksReading(true);
                        const answers = new Map(await Promise.all(tankNos.map((no) => tankOf(no).then((answer) => [no, answer]))));
                        if (seq !== fetchSeq.current) return;   // another contract was typed meanwhile
                        setTanksReading(false);
                        const tanks = tanksOfHistory(tankRows, answers);   // { [row id]: litres } - Ejar's tanks only
                        const tankIds = Object.keys(tanks);
                        if (tankIds.length > 0) {
                            setVehicleLogs(prev => {
                                const next = { ...prev };
                                tankIds.forEach((id) => { next[id] = { ...(next[id] || { out: 0, in: 0 }), capacity: tanks[id] }; });
                                return next;
                            });
                            setEjarTanks(Object.fromEntries(tankIds.map((id) => [id, true])));
                        }
                        // the single row: the same Ejar tank; none - typed for this calculation only
                        if (history.length === 1 && tanks[history[0].id]) {
                            setTankCapacity(tanks[history[0].id]);
                            setTankFromEjar(true);
                        }
                    }
                } else {
                    setContractData(null);
                    setContractHistory([]);
                    setContractSource('');
                    setError(' No data found for the entered number');
                    setSyncedFields({});
                }
            } catch (err) {
                logger.error('Error fetching contract in calculator', err);
                setError('Error fetching data');
            }
        };

        const timeoutId = setTimeout(() => {
            if (booking) fetchContracts();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        fetchSeq.current += 1;   // a tank answer still coming for the contract typed before is dropped
        setTanksReading(false);
        if (booking.trim() !== '') {
            setLogs([]);
            setOut(0); setInVal(0); setTankCapacity(''); setTankFromEjar(false);
            setVehicleLogs({});
            setEjarTanks({});
            setSyncedFields({});
        }
    }, [booking]);

    const handleAddLog = () => {
        const logDate = lastDate || new Date().toISOString().split('T')[0];
        if (tankCapacity === '') {
            setInputError('Please enter all fields.');
            return;
        }
        const cap = Number(tankCapacity);
        if (isNaN(cap) || cap <= 0) {
            setInputError('Tank Capacity must be a positive number.');
            return;
        }
        if (out < inVal) {
            setInputError('OUT (Start) cannot be less than IN (End) for fuel consumption.');
            return;
        }
        
        const diffLiters = ((out - inVal) / 100) * cap;
        
        const plate = contractData?.['Plate No.'] || contractData?.['Plate Number'] || '';
        
        setLogs([...logs, {
            date: logDate,
            out: out,
            inVal: inVal,
            capacity: cap,
            usedLiters: diffLiters,
            plate: plate,
            model: contractData?.['Model'] || ''
        }]);

        // the tank is never saved by model any more (owner, 28 Sep 2026): Ejar's own figure, or typed for this one
        setOut(0); setInVal(0); setTankCapacity(''); setTankFromEjar(false);
        setInputError('');
    };

    const handleVehicleLogChange = (contractId, field, value) => {
        setVehicleLogs(prev => ({
            ...prev,
            [contractId]: {
                ...prev[contractId],
                [field]: value
            }
        }));
        // Remove sync indicator if user manually changes the field
        setSyncedFields(prev => {
            const newState = { ...prev };
            delete newState[`${contractId}_${field}`];
            return newState;
        });
    };

    // the earliest contract's own Pick-up Date text (history comes oldest first, so the first of a day is its earliest time)
    const getFirstPickup = () => {
        if (contractHistory.length === 0) return null;
        const sorted = [...contractHistory].sort((a, b) => {
            const dA = parseCustomDate(a['Pick-up Date']) || '9999-99-99';
            const dB = parseCustomDate(b['Pick-up Date']) || '9999-99-99';
            return dA.localeCompare(dB);
        });
        return sorted[0]['Pick-up Date'];
    };
    const getFirstDate = () => parseCustomDate(getFirstPickup());

    // A contract start / end date ('yyyy-mm-dd' state) as shown: the contract's own text with its time
    // while it is that same day, else the date alone (today for an open contract has no time)
    const shownWithTime = (ymd, raw) => (ymd && typeof raw === 'string' && parseCustomDate(raw) === ymd ? showContractDate(raw) : '')
        || formatDateToDMY(ymd);

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Fuel Report');
        addReportHeader(sheet, {
            title: 'Fuel Report - ' + (contractData?.['Booking Number'] || ''),
            customer: contractData?.['Customer'],
            period: (shownWithTime(lastDate, getFirstPickup()) || 'N/A') + ' - ' + (shownWithTime(manualEndDate, endRaw) || 'N/A'),
        });
        sheet.addRow(['#', 'Vehicle Plate', 'Type', 'Out (%)', 'In (%)', 'Capacity (L)', 'Used (L)', 'Period']);

        let total = 0;
        contractHistory.forEach((c, idx) => {
            const log = vehicleLogs[c.id];
            if (log && log.out !== undefined && log.in !== undefined && (log.capacity !== undefined && log.capacity !== '')) {
                const diff = ((Number(log.out) - Number(log.in)) / 100) * Number(log.capacity);
                total += diff;
                sheet.addRow([
                    idx + 1, 
                    c['Plate No.'] || '', 
                    c['Model'] || '', 
                    log.out + '%', 
                    log.in + '%', 
                    log.capacity, 
                    diff.toFixed(2), 
                    (showContractDate(c['Pick-up Date']) || 'N/A') + ' - ' + (showContractDate(c['Drop-off Date']) || 'Active')
                ]);
            }
        });

        sheet.addRow([]);
        sheet.addRow(['SUMMARY']);
        sheet.addRow(['Total Used:', total.toFixed(2) + ' Liters']);
        sheet.addRow(['Total Amount:', (total * FUEL_PRICE_PER_LITER).toFixed(2) + ' AED']);

        await saveWorkbook(workbook, `Fuel-Report-${contractData?.['Booking Number'] || 'Report'}.xlsx`);
    };

    const handleRefSubmit = async () => {
        let activeLogs = [];
        let totalUsedLiters = 0;

        contractHistory.forEach(c => {
            const log = vehicleLogs[c.id];
            if (log && log.out !== undefined && log.in !== undefined && (log.capacity !== undefined && log.capacity !== '')) {
                const diff = ((Number(log.out) - Number(log.in)) / 100) * Number(log.capacity);
                totalUsedLiters += diff;
                activeLogs.push({
                    desc: `${c['Model'] || ''} (${c['Plate No.'] || ''})`,
                    out: log.out,
                    inVal: log.in,
                    cap: log.capacity,
                    diff: diff,
                    model: c['Model']
                });
            }
        });

        // Net / VAT 5% / Total, each rounded to fils (the shared desk invoice - DUP-22)
        const amounts = deskInvoiceAmounts(totalUsedLiters * FUEL_PRICE_PER_LITER);

        const customerName = contractData['Customer'] || 'Customer';
        const bookingId = contractData['Booking Number'] || '';
        const contractNo = (contractData['Contract No.'] || '').split('-')[0] || '';
        const model = contractData['Car Model'] || contractData['Model'] || '';
        const plateNo = contractData['Plate Number'] || contractData['Plate No.'] || '';
        const vehicle = model && plateNo ? `${model} - ${plateNo}` : (model || plateNo || 'Vehicle');

        // The number through the one invoice-number service (today: ALWFQ-F-<typed>, as before)
        let refNumber;
        try {
            [refNumber] = await number.claim(1, currentUser?.email || '');
        } catch {
            return; // the dialog shows why
        }

        const html = deskInvoiceHtml({
            title: 'Fuel Tax Invoice',
            date: new Date().toLocaleDateString('en-GB'),
            ref: refNumber,
            trn: companySettings.trn,
            subject: 'SUB: Fuel Charges',
            greeting: ['We thank you for your business renting the below vehicle;'],
            amountLabel: 'Fuel Amount',
            amounts,
            description: [
                `<strong>Customer:</strong> ${escapeHtml(customerName)}`,
                `<strong>Booking ID:</strong> ${escapeHtml(bookingId)}`,
                `<strong>R/A:</strong> ${escapeHtml(contractNo)}`,
                `<strong>Vehicle:</strong> ${escapeHtml(contractHistory.length > 1 ? 'Multiple Vehicles' : vehicle)}`,
                '',
                ...activeLogs.map((log) => `<strong>${escapeHtml(log.desc)}</strong> [${escapeHtml(log.cap)}L] Out: ${escapeHtml(log.out)}% | In: ${escapeHtml(log.inVal)}% => ${log.diff.toFixed(2)}L`),
                '',
                `<strong>Total Used:</strong> ${totalUsedLiters.toFixed(2)} Liters x AED ${escapeHtml(FUEL_PRICE_PER_LITER)}`,
            ],
        });

        setShowRefModal(false);
        try {
            await saveDeskInvoicePdf(html, `Fuel-Tax-Invoice-${bookingId || 'Unknown'}.pdf`);
        } catch (error) {
            logger.error('Failed to generate PDF:', error);
            toast.error('Failed to generate PDF.');
            return;
        }

        toast.success('Generated Fuel Invoice successfully!');
        adminService.logUserAction('GENERATE_REPORT', 'Generated Fuel Invoice (PDF)', currentUser, { bookingId, amount: amounts.total, reference: refNumber });
    };

    const handleCalculateTotal = () => {
        const newLogs = [];
        let hasError = false;
        contractHistory.forEach(c => {
            const log = vehicleLogs[c.id];
            if (log && log.out !== undefined && log.in !== undefined && (log.capacity !== undefined && log.capacity !== '')) {
                const outNum = Number(log.out);
                const inNum = Number(log.in);
                const cap = Number(log.capacity);

                if (isNaN(cap) || cap <= 0) {
                    setInputError(`Invalid Capacity for ${c['Plate No.'] || 'Car'}`);
                    hasError = true;
                } else if (outNum < inNum) {
                    setInputError(`OUT (Start) < IN (End) for ${c['Plate No.'] || 'Car'}`);
                    hasError = true;
                } else {
                    const diff = ((outNum - inNum) / 100) * cap;
                    newLogs.push({
                        date: c['Pick-up Date'] || 'N/A',
                        out: outNum,
                        inVal: inNum,
                        capacity: cap,
                        usedLiters: diff,
                        plate: c['Plate No.'] || c['Plate Number'] || '',
                        model: c['Model'] || ''
                    });
                }
            }
        });

        if (hasError) return;
        if (newLogs.length === 0) {
            setInputError('Please enter fuel data for at least one vehicle.');
            return;
        }
        setLogs(newLogs);
        setInputError('');
    };

    const fuelOptions = FUEL_STREAKS.map(s => ({ value: s, label: `${s}%` }));
    const syncedMark = <Icon glyph={ICONS.cloud_sync} size="xs" tone="info" label="Synced from Archive" title="Synced from Archive" />;
    const eCheckLink = (num) => (
        <a
            href={`https://ejarprod.iyelo.com:6300/app/fleet-management/e-check/e-check-list/view/${num}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:underline"
        >
            Open E-Check <Icon glyph={ICONS.arrowRight} size="xs" />
        </a>
    );
    const totalLiters = logs.reduce((acc, l) => acc + l.usedLiters, 0);

    return (
        <div className="flex flex-col gap-4">
            <Card
                title="Fuel calculator"
                subtitle="Fuel used between the OUT and IN check of each car · the tank size comes from the Ejar contract"
            >
                <div className="flex flex-col gap-6">
                    <div className="max-w-md">
                        <Field label="Booking or Contract Number" error={error ? error.trim() : undefined}>
                            <Input
                                value={booking}
                                onChange={(e) => setBooking(e.target.value)}
                                placeholder="Enter Booking or Contract #"
                                prefix={<Icon glyph={ICONS.search} size="sm" />}
                            />
                        </Field>
                    </div>

                    {contractData && (
                        <Card><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8">
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">Contract details</p>
                                <p className="text-sm"><span className="text-gray-600">Contract #:</span> <span className="font-mono font-medium text-gray-900">{contractData['Contract No.'] || 'N/A'}</span></p>
                                {contractData['Booking Number'] && (
                                    <p className="text-sm"><span className="text-gray-600">Booking ID:</span> <span className="text-gray-900">{contractData['Booking Number']}</span></p>
                                )}
                            </div>

                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">Customer</p>
                                <p className="text-sm font-semibold text-gray-900">{contractData['Customer'] || 'Unknown Customer'}</p>
                                <p className="text-sm text-gray-600">
                                    Status: <StatusBadge status={contractSource ? contractSource.split(' ')[0] : ''} label={contractSource || 'N/A'} />
                                </p>
                            </div>

                            <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-500">Timeline & vehicles</p>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                                    <p><span className="text-gray-600">Start:</span> {shownWithTime(getFirstDate(), getFirstPickup()) || 'N/A'}</p>
                                    <p><span className="text-gray-600">End:</span> {shownWithTime(manualEndDate, endRaw) || 'N/A'}</p>
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                    <span className="text-gray-600">Cars ({contractHistory.length}):</span> {
                                        contractHistory.map(c => `${c['Model'] || 'Car'} - ${c['Plate No.'] || c['Plate Number'] || ''}`).join(', ')
                                    }
                                </p>
                            </div>
                        </div></Card>
                    )}

                    {contractHistory.length > 0 && (
                        <section className="max-w-4xl">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Vehicle fuel log</h3>
                            <div className="flex flex-col gap-3">
                                {contractHistory.map((c) => {
                                    const log = vehicleLogs[c.id] || { out: 0, in: 0, capacity: '' };
                                    const liveLiters = (log.out !== undefined && log.in !== undefined && log.capacity)
                                        ? (((Number(log.out) - Number(log.in)) / 100) * Number(log.capacity)).toFixed(2)
                                        : '0.00';

                                    return (
                                        <Card key={c.id}><div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                                            <div>
                                                <div className="font-medium text-gray-900">{c['Model'] || 'Car'}</div>
                                                <div className="text-xs text-gray-500 font-mono">{c['Plate No.'] || ''}</div>
                                            </div>
                                            <div>
                                                <Field label="Start (%)" labelAction={syncedFields[`${c.id}_out`] ? syncedMark : null}>
                                                    <Select
                                                        size="sm"
                                                        value={log.out}
                                                        onChange={(e) => handleVehicleLogChange(c.id, 'out', Number(e.target.value))}
                                                        options={fuelOptions}
                                                    />
                                                </Field>
                                                {c['E-Check Number Out'] && eCheckLink(c['E-Check Number Out'])}
                                            </div>
                                            <div>
                                                <Field label="End (%)" labelAction={syncedFields[`${c.id}_in`] ? syncedMark : null}>
                                                    <Select
                                                        size="sm"
                                                        value={log.in}
                                                        onChange={(e) => handleVehicleLogChange(c.id, 'in', Number(e.target.value))}
                                                        options={fuelOptions}
                                                    />
                                                </Field>
                                                {c['E-Check Number In'] && eCheckLink(c['E-Check Number In'])}
                                            </div>
                                            <div>
                                                {ejarTanks[c.id] && log.capacity ? (
                                                    <Field label="Tank capacity (L)">
                                                        <div className="flex items-center h-8 px-2 rounded-md border border-gray-200 bg-gray-50">
                                                            <span className="font-medium text-gray-900 tabular-nums">{ejarTankText(log.capacity)}</span>
                                                        </div>
                                                    </Field>
                                                ) : (
                                                    <Field label="Tank capacity (L)" help={tanksReading ? TANK_READING_HINT : NO_TANK_HINT}>
                                                        <Input
                                                            size="sm"
                                                            type="number"
                                                            value={log.capacity || ''}
                                                            onChange={(e) => handleVehicleLogChange(c.id, 'capacity', e.target.value)}
                                                            placeholder="Liters"
                                                            suffix="L"
                                                        />
                                                    </Field>
                                                )}
                                            </div>
                                            <div className="md:text-right">
                                                <div className="text-xs font-medium text-gray-600">Liters</div>
                                                <div className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{liveLiters} L</div>
                                            </div>
                                        </div></Card>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Single Vehicle Manual Input (Fallback) */}
                    {contractHistory.length === 0 && (
                        <Card className="max-w-2xl">
                            <FormGrid cols={3}>
                                <Field label="Start (%)" labelAction={Object.keys(syncedFields).some(k => k.endsWith('_out')) ? syncedMark : null}>
                                    <Select value={out} onChange={(e) => setOut(Number(e.target.value))} options={fuelOptions} />
                                </Field>
                                <Field label="End (%)" labelAction={Object.keys(syncedFields).some(k => k.endsWith('_in')) ? syncedMark : null}>
                                    <Select value={inVal} onChange={(e) => setInVal(Number(e.target.value))} options={fuelOptions} />
                                </Field>
                                {tankCapacity && tankFromEjar ? (
                                    <Field label="Tank capacity (L)">
                                        <div className="flex items-center h-9 px-3 rounded-md border border-gray-200 bg-gray-50">
                                            <span className="font-medium text-gray-900 tabular-nums">{ejarTankText(tankCapacity)}</span>
                                        </div>
                                    </Field>
                                ) : (
                                    <Field label="Tank capacity (L)" help={tanksReading ? TANK_READING_HINT : NO_TANK_HINT}>
                                        <Input value={tankCapacity} onChange={(e) => setTankCapacity(e.target.value)} type="number" placeholder="Liters" suffix="L" />
                                    </Field>
                                )}
                            </FormGrid>
                            <div className="flex justify-end mt-4">
                                <Button onClick={handleAddLog} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>Add Entry</Button>
                            </div>
                        </Card>
                    )}

                    {inputError && <p className="text-sm text-red-700" role="alert">{inputError}</p>}

                    <RequirePermission permission={PERMISSIONS.ops_fuel_add_trip} fallback={null}>
                        <div>
                            {contractHistory.length > 0 ? (
                                <Button variant="primary" onClick={handleCalculateTotal} leadingIcon={<Icon glyph={ICONS.calculator} size="sm" />}>
                                    Calculate Total Usage
                                </Button>
                            ) : (
                                <Button variant="primary" onClick={handleAddLog} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                    Add Entry
                                </Button>
                            )}
                        </div>
                    </RequirePermission>

                    {/* Simple Liters calculation display */}
                    {logs.length > 0 && (
                        <div className="flex flex-col gap-3">
                            <StatGrid min={200}>
                                <Stat label="Total fuel used" value={`${totalLiters.toFixed(2)} Liters`} icon={ICONS.engine} />
                                <Stat label={`Total amount (price: ${FUEL_PRICE_PER_LITER})`} value={`AED ${(totalLiters * FUEL_PRICE_PER_LITER).toFixed(2)}`} icon={ICONS.wallet} />
                            </StatGrid>
                            <div className="ds-toolbar">
                                <Button onClick={exportToExcel} leadingIcon={<Icon glyph={ICONS.spreadsheet} size="sm" />}>Export Excel</Button>
                                <RequirePermission permission={PERMISSIONS.ops_fuel_generate_invoice} fallback={null}>
                                    <Button onClick={() => setShowRefModal(true)} leadingIcon={<Icon glyph={ICONS.invoices} size="sm" />}>Generate Invoice</Button>
                                </RequirePermission>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            <Dialog
                open={showRefModal}
                onClose={() => setShowRefModal(false)}
                title="Confirm fuel invoice"
                size="sm"
                closeOnOverlay={false}
                footer={(
                    <>
                        <Button onClick={() => setShowRefModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleRefSubmit}>Generate</Button>
                    </>
                )}
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-700">Total Amount: <span className="font-semibold text-gray-900 tabular-nums">AED {deskInvoiceAmounts(totalLiters * FUEL_PRICE_PER_LITER).total.toFixed(2)}</span> (Inc. VAT)</p>
                    <InvoiceNumberField state={number} label="Reference #" placeholder="Reference #" prefix="" />
                </div>
            </Dialog>
        </div>
    );
};

export default FuelCalculator;
